
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Send, AlertCircle, Info, Database, ExternalLink } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function InfradigitalConfigDebugger() {
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const { toast } = useToast();

  const fetchInfradigitalConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('infradigital_config')
        .select('key, value');
      
      if (error) {
        throw new Error(`Failed to fetch configuration: ${error.message}`);
      }
      
      const redactedData = data.map(item => ({
        key: item.key,
        value: item.key.toLowerCase().includes('token') || 
               item.key.toLowerCase().includes('pass') || 
               item.key.toLowerCase().includes('secret') ? 
               '********' : item.value
      }));
      
      setConfigData(redactedData);
      toast({
        title: "Success",
        description: "Configuration fetched successfully",
      });
    } catch (error: any) {
      console.error('Error fetching config:', error);
      setError(error.message || "Failed to fetch configuration");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to fetch configuration",
      });
    } finally {
      setLoading(false);
    }
  };

  const testApiEndpoint = async () => {
    setLoading(true);
    setError(null);
    setApiResponse(null);
    try {
      const apiUrlConfig = configData?.find((item: any) => item.key === 'CFS_API_URL');
      if (!apiUrlConfig) {
        throw new Error('API URL configuration not found');
      }
      
      const testPayload = {
        "create_by": "debug@test.com",
        "biller_code": "60000",
        "bill_key": "12345", 
        "account_code": "MANDIRI_IDN",
        "bill_component_name": "DEBUG_TEST",
        "expiry_date": "2025-12-31T23:59:59Z",
        "due_date": "2025-12-15T23:59:59Z",
        "active_date": "2025-12-01T00:00:00Z",
        "amount": 10.00,
        "amount_currency": "IDR",
        "penalty_amount": 0,
        "batch_id": "DEBUG_SESSION",
        "notes": "Test from config debugger"
      };
      
      const { data, error } = await supabase.functions.invoke('debug-bill-component', {
        body: { 
          test: true,
          payload: testPayload
        }
      });
      
      if (error) {
        throw new Error(`API test failed: ${error.message}`);
      }
      
      setApiResponse(data);
      toast({
        title: "API Test Complete",
        description: data.success ? "API connection successful" : "API connection failed",
      });
    } catch (error: any) {
      console.error('API test error:', error);
      setError(error.message || "API test failed");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "API test failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Infradigital Configuration Debugger
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={fetchInfradigitalConfig}
              disabled={loading}
              variant="outline"
            >
              {loading ? "Loading..." : "Fetch Config"}
              <Info className="ml-2 h-4 w-4" />
            </Button>
            
            <Button 
              onClick={testApiEndpoint}
              disabled={loading || !configData}
              variant="outline"
            >
              {loading ? "Testing..." : "Test API Endpoint"}
              <Send className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="mt-2">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {configData && (
            <div className="mt-6">
              <h3 className="font-semibold mb-2">Configuration:</h3>
              <div className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-auto max-h-96">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2">Key</th>
                      <th className="text-left py-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configData.map((item: any, index: number) => (
                      <tr key={index} className="border-b border-gray-200 dark:border-gray-800">
                        <td className="py-2 font-mono text-sm">{item.key}</td>
                        <td className="py-2 font-mono text-sm">{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {configData.find((item: any) => item.key === 'CFS_API_URL') && (
                <Alert className="mt-4" variant="default">
                  <Info className="h-4 w-4" />
                  <AlertTitle>API URL Configuration</AlertTitle>
                  <AlertDescription className="mt-2">
                    The API URL will be used exactly as configured in the database without any modifications.
                    <p className="mt-2 font-mono text-xs">
                      Make sure your CFS_API_URL is set to the full endpoint URL: https://sg.infradigital.net/pordi-proxy-mr/v1/bill_component/create
                    </p>
                    <p className="mt-2 text-xs text-red-500">
                      If you're getting 404 errors, double-check that this URL exactly matches the one working in Postman.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          {apiResponse && (
            <div className="mt-6">
              <Separator className="my-4" />
              <h3 className="font-semibold mb-2">API Test Response:</h3>
              <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                {JSON.stringify(apiResponse, null, 2)}
              </pre>
              
              {apiResponse?.request?.url && (
                <Alert className="mt-4" variant={apiResponse.success ? "default" : "destructive"}>
                  <ExternalLink className="h-4 w-4" />
                  <AlertTitle>API Request Details</AlertTitle>
                  <AlertDescription className="mt-2">
                    <p><strong>URL used:</strong> <span className="font-mono text-xs">{apiResponse.request.url}</span></p>
                    {!apiResponse.success && (
                      <div className="mt-2">
                        <p className="text-red-500 font-semibold">Troubleshooting 404 Errors:</p>
                        <ul className="list-disc pl-5 mt-1 text-xs space-y-1">
                          <li>Compare this URL exactly with what's working in Postman</li>
                          <li>Check if the school ID (bill_key) is registered in Infradigital</li>
                          <li>Verify the authentication token is correct and not expired</li>
                          <li>Ensure there are no trailing slashes or formatting differences</li>
                        </ul>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
